import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { usePrivacy } from "@/context/PrivacyContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { fmtCurrency, fmtDate } from "@/lib/format";
import { CopyX } from "lucide-react";
import { toast } from "sonner";

export function DuplicateCleanup() {
    const qc = useQueryClient();
    const { mask } = usePrivacy();
    const [open, setOpen] = useState(false);
    const [items, setItems] = useState(null);
    const [working, setWorking] = useState(false);

    const load = async () => {
        setOpen(true); setItems(null);
        try { setItems((await api.get("/dividends/duplicates/preview")).data.duplicates); }
        catch (e) { toast.error(apiErr(e)); setOpen(false); }
    };

    const apply = async () => {
        setWorking(true);
        try {
            const { data } = await api.post("/dividends/duplicates/cleanup", { ids: items.map((d) => d.id) });
            toast.success(`Removed ${data.deleted} duplicate dividend${data.deleted === 1 ? "" : "s"}`);
            setOpen(false); qc.invalidateQueries();
        } catch (e) { toast.error(apiErr(e)); } finally { setWorking(false); }
    };

    return (
        <>
            <Button size="sm" variant="outline" onClick={load} className="border-amber-500/40 text-amber-700" data-testid="remove-duplicates-btn">
                <CopyX className="w-3.5 h-3.5 mr-1.5" />Remove duplicates
            </Button>
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-lg" data-testid="duplicates-dialog">
                    <DialogHeader><DialogTitle className="font-heading">Remove duplicate dividends</DialogTitle></DialogHeader>
                    {!items ? <div className="py-8 text-center text-sm text-muted-foreground">Scanning dividends…</div>
                        : items.length === 0 ? <div className="py-8 text-center text-sm text-muted-foreground" data-testid="duplicates-empty">No duplicates found.</div>
                        : (
                            <>
                                <p className="text-sm text-muted-foreground">These {items.length} hand-entered dividend{items.length === 1 ? "" : "s"} match an auto-recorded payout. The auto-recorded copy (exact ex/pay dates and tax) is kept; yours will be deleted.</p>
                                <div className="max-h-72 overflow-y-auto text-xs space-y-1" data-testid="duplicates-list">
                                    {items.map((d) => (
                                        <div key={d.id} className="flex items-center justify-between gap-2 border-b border-border/40 py-1.5 num" data-testid={`duplicate-${d.id}`}>
                                            <span className="font-medium">{d.ticker} · {d.sub_portfolio}</span>
                                            <span className="text-right">
                                                <span className="text-loss line-through">{fmtDate(d.date)} {mask(fmtCurrency(d.amount, { decimals: 2 }))}</span>
                                                <span className="text-muted-foreground"> → keep ex {fmtDate(d.kept_ex_date)} {mask(fmtCurrency(d.kept_amount, { decimals: 2 }))}</span>
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </>
                        )}
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
                        {items?.length > 0 && (
                            <Button onClick={apply} disabled={working} className="bg-loss text-white hover:bg-loss/90" data-testid="duplicates-apply-btn">
                                {working ? "Removing…" : `Delete ${items.length}`}
                            </Button>
                        )}
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}
